// ============================================================================
// WORK ITEMS TIMELINE/GANTT VIEW
// Project timeline visualization with dependency arrows, critical path,
// milestone markers, drag-to-move, and auto-scheduling
// Date: October 13, 2025 | Enhanced: April 2026
// ============================================================================

'use client';

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { format, differenceInDays, addDays, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { useResponsive } from '@/hooks/useResponsive';
import { getKanbanStylePreset } from '@/components/kanban/KanbanStylePresets';
import BrandedLoadingSpinner, { InlineLoadingPill } from '@/components/shared/BrandedLoadingSpinner';
import WorkItemHoverCard from '@/components/work-items/WorkItemHoverCard';
import type { WorkItem } from './UniversalWorkItems';
import type { WorkItemTemplate } from './WorkItemModal';
import { ChevronLeft, ChevronRight, Calendar, Diamond, GitBranch, Info, Route } from 'lucide-react';
import { getWorkItemTypeAccent, getWorkItemTypeIdentity } from '@/lib/work-items/typeIdentity';
import {
  getWorkItemsAccentPanelStyle,
  WorkItemsTypeAccentBadge,
} from '@/components/work-items/WorkItemsViewAccent';

// ============================================================================
// TYPES
// ============================================================================

export interface DependencyLink {
  sourceId: string;
  targetId: string;
  type: 'depends_on' | 'blocks';
  relationshipId?: string;
}

interface TimelineItem extends WorkItem {
  startDate: Date;
  endDate: Date;
  dependencies?: string[];
  isMilestone?: boolean;
}

type TimelineSourceItem = WorkItem & {
  start_date?: string | null;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  estimated_hours?: number | string | null;
};

interface DragState {
  itemId: string;
  type: 'move' | 'resize-start' | 'resize-end';
  startX: number;
  originalStartDate: Date;
  originalEndDate: Date;
}

interface WorkItemsTimelineViewProps {
  items: WorkItem[];
  onUpdateDates: (itemId: string, startDate: string, endDate: string) => void;
  onViewItem: (item: WorkItem) => void;
  loading?: boolean;
  templates?: WorkItemTemplate[];
  stylePreset?: string;
  activeItemType?: string | null;
  activeItemTypeLabel?: string;
  primaryColor?: string;
  dependencies?: DependencyLink[];
  showCriticalPath?: boolean;
  groupByType?: boolean;
  onCreateDependency?: (sourceId: string, targetId: string) => void;
  onDateCascade?: (itemId: string, newStartDate: string, newEndDate: string) => void;
  highlightedItemId?: string;
}

// ============================================================================
// CRITICAL PATH ALGORITHM
// ============================================================================

function computeCriticalPath(items: TimelineItem[], deps: DependencyLink[]): Set<string> {
  if (!deps.length || items.length < 2) return new Set();

  const itemIds = new Set(items.map((item) => item.id));
  const successors = new Map<string, string[]>();
  const predecessors = new Map<string, string[]>();

  for (const dep of deps) {
    if (!itemIds.has(dep.sourceId) || !itemIds.has(dep.targetId)) continue;
    const predecessorId = dep.type === 'depends_on' ? dep.targetId : dep.sourceId;
    const successorId = dep.type === 'depends_on' ? dep.sourceId : dep.targetId;

    if (!successors.has(predecessorId)) successors.set(predecessorId, []);
    successors.get(predecessorId)!.push(successorId);
    if (!predecessors.has(successorId)) predecessors.set(successorId, []);
    predecessors.get(successorId)!.push(predecessorId);
  }

  const duration = new Map<string, number>();
  const earliestStart = new Map<string, number>();
  const earliestFinish = new Map<string, number>();

  for (const item of items) {
    const days = Math.max(1, differenceInDays(item.endDate, item.startDate) + 1);
    duration.set(item.id, days);
    earliestStart.set(item.id, 0);
    earliestFinish.set(item.id, days);
  }

  const inDegree = new Map<string, number>();
  for (const id of itemIds) inDegree.set(id, 0);
  for (const [, values] of successors) {
    for (const id of values) inDegree.set(id, (inDegree.get(id) || 0) + 1);
  }

  const queue: string[] = [];
  for (const [id, degree] of inDegree) {
    if (degree === 0) queue.push(id);
  }

  const topoOrder: string[] = [];
  while (queue.length > 0) {
    const current = queue.shift()!;
    topoOrder.push(current);
    for (const successor of successors.get(current) || []) {
      const nextDegree = (inDegree.get(successor) || 1) - 1;
      inDegree.set(successor, nextDegree);
      if (nextDegree === 0) queue.push(successor);
    }
  }

  for (const nodeId of topoOrder) {
    const prior = predecessors.get(nodeId) || [];
    let maxFinish = 0;
    for (const predecessor of prior) {
      maxFinish = Math.max(maxFinish, earliestFinish.get(predecessor) || 0);
    }
    earliestStart.set(nodeId, maxFinish);
    earliestFinish.set(nodeId, maxFinish + (duration.get(nodeId) || 1));
  }

  const projectEnd = Math.max(...Array.from(earliestFinish.values()));
  const latestStart = new Map<string, number>();
  const latestFinish = new Map<string, number>();
  for (const id of itemIds) latestFinish.set(id, projectEnd);

  for (let index = topoOrder.length - 1; index >= 0; index -= 1) {
    const nodeId = topoOrder[index];
    const nextItems = successors.get(nodeId) || [];
    let minStart = projectEnd;
    for (const successor of nextItems) {
      minStart = Math.min(minStart, latestStart.get(successor) ?? projectEnd);
    }
    latestFinish.set(nodeId, minStart);
    latestStart.set(nodeId, minStart - (duration.get(nodeId) || 1));
  }

  const criticalItems = new Set<string>();
  for (const id of itemIds) {
    const slack = (latestStart.get(id) || 0) - (earliestStart.get(id) || 0);
    if (Math.abs(slack) < 0.5) {
      criticalItems.add(id);
    }
  }

  return criticalItems;
}

const ROW_HEIGHT = 52;

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function WorkItemsTimelineView({
  items,
  onUpdateDates,
  onViewItem,
  loading = false,
  templates = [],
  stylePreset,
  activeItemType,
  activeItemTypeLabel,
  primaryColor = '#3b82f6',
  dependencies = [],
  showCriticalPath = false,
  groupByType = false,
  onDateCascade,
  highlightedItemId,
}: WorkItemsTimelineViewProps) {
  const presetViews = getKanbanStylePreset(stylePreset).views;
  const { isMobile } = useResponsive();
  const [viewStart, setViewStart] = useState(startOfMonth(new Date()));
  const [viewEnd, setViewEnd] = useState(endOfMonth(new Date()));
  const [zoomLevel, setZoomLevel] = useState<'day' | 'week' | 'month'>('week');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [dragPreview, setDragPreview] = useState<{ itemId: string; startDate: Date; endDate: Date } | null>(null);
  const [showDeps, setShowDeps] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const activeTypeIdentity = useMemo(
    () => getWorkItemTypeIdentity(activeItemType, primaryColor),
    [activeItemType, primaryColor]
  );
  const activeTypeAccent = useMemo(
    () => getWorkItemTypeAccent(activeItemType, primaryColor),
    [activeItemType, primaryColor]
  );
  const resolvedActiveTypeLabel = activeItemTypeLabel || activeTypeIdentity.label;

  useEffect(() => {
    const element = timelineContainerRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const getTemplate = useCallback((item: WorkItem): WorkItemTemplate | null => {
    if (!item.item_subtype || templates.length === 0) return null;
    return templates.find((template) => template.item_subtype === item.item_subtype) || null;
  }, [templates]);

  const timelineItems = useMemo<TimelineItem[]>(() => {
    const mapped = items
      .filter((item) => {
        const sourceItem = item as TimelineSourceItem;
        return Boolean(item.due_date || sourceItem.start_date || sourceItem.scheduled_start);
      })
      .map((item) => {
        const sourceItem = item as TimelineSourceItem;
        const rawStart = sourceItem.start_date || sourceItem.scheduled_start;
        const rawEnd = item.due_date || sourceItem.scheduled_end;
        const endDate = rawEnd ? new Date(rawEnd) : new Date();

        let startDate: Date;
        if (rawStart) {
          startDate = new Date(rawStart);
        } else {
          const estimatedHours = Number(sourceItem.estimated_hours || 0);
          const estimatedDays = estimatedHours > 0 ? Math.max(1, Math.ceil(estimatedHours / 8)) : 3;
          startDate = addDays(endDate, -estimatedDays);
        }

        if (startDate >= endDate) {
          startDate = addDays(endDate, -1);
        }

        return {
          ...item,
          startDate,
          endDate,
          isMilestone: item.item_type === 'milestone',
        };
      });

    if (groupByType) {
      const typeOrder: Record<string, number> = { milestone: 0, project: 1, job: 2, task: 3, appointment: 4, ticket: 5 };
      mapped.sort((left, right) => {
        const leftOrder = typeOrder[left.item_type] ?? 99;
        const rightOrder = typeOrder[right.item_type] ?? 99;
        if (leftOrder !== rightOrder) return leftOrder - rightOrder;
        return left.startDate.getTime() - right.startDate.getTime();
      });
    } else {
      mapped.sort((left, right) => left.startDate.getTime() - right.startDate.getTime());
    }

    return mapped;
  }, [groupByType, items]);

  const visibleItems = useMemo(() => {
    if (!groupByType || collapsedGroups.size === 0) return timelineItems;
    return timelineItems.filter((item) => !collapsedGroups.has(item.item_type));
  }, [collapsedGroups, groupByType, timelineItems]);

  const displayItems = useMemo(() => {
    if (!dragPreview) return visibleItems;
    return visibleItems.map((item) => item.id === dragPreview.itemId
      ? { ...item, startDate: dragPreview.startDate, endDate: dragPreview.endDate }
      : item
    );
  }, [dragPreview, visibleItems]);

  const criticalPathIds = useMemo(() => {
    if (!showCriticalPath || dependencies.length === 0) return new Set<string>();
    return computeCriticalPath(timelineItems, dependencies);
  }, [dependencies, showCriticalPath, timelineItems]);

  const timelineDays = useMemo(() => eachDayOfInterval({ start: viewStart, end: viewEnd }), [viewEnd, viewStart]);

  const visibleDependencyCount = useMemo(() => {
    if (dependencies.length === 0) return 0;
    const visibleIds = new Set(displayItems.map((item) => item.id));
    return dependencies.filter((dependency) => visibleIds.has(dependency.sourceId) && visibleIds.has(dependency.targetId)).length;
  }, [dependencies, displayItems]);

  const rangeLabel = useMemo(() => {
    return isMobile
      ? `${format(viewStart, 'MMM d')} - ${format(viewEnd, 'MMM d')}`
      : `${format(viewStart, 'MMM d, yyyy')} - ${format(viewEnd, 'MMM d, yyyy')}`;
  }, [isMobile, viewEnd, viewStart]);

  const hasDesktopToolbarActiveType = Boolean(activeItemType && !isMobile);

  const summaryBadgeClassName = `${presetViews.buttonBg} ${presetViews.buttonText} inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-medium`;

  const formatItemRange = useCallback((item: TimelineItem) => {
    const startLabel = format(item.startDate, 'd MMM');
    const endLabel = format(item.endDate, 'd MMM');
    const duration = Math.max(1, differenceInDays(item.endDate, item.startDate) + 1);

    return {
      label: startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`,
      durationLabel: `${duration} day${duration === 1 ? '' : 's'}`,
    };
  }, []);

  const getBarStyle = useCallback((item: TimelineItem) => {
    const totalDays = Math.max(1, differenceInDays(viewEnd, viewStart));
    const dayWidth = 100 / totalDays;
    const startOffset = Math.max(0, differenceInDays(item.startDate, viewStart));
    const duration = Math.max(1, differenceInDays(item.endDate, item.startDate) + 1);
    const left = startOffset * dayWidth;
    const width = duration * dayWidth;

    return {
      left: `${left}%`,
      width: `${Math.min(width, 100 - left)}%`,
    };
  }, [viewEnd, viewStart]);

  const getColorByPriority = useCallback((priority?: string, template?: WorkItemTemplate | null) => {
    if (template?.color) {
      return { bg: template.color, border: template.color, text: 'white' };
    }

    switch (priority || '') {
      case 'critical': return { bg: '#dc2626', border: '#991b1b', text: 'white' };
      case 'urgent': return { bg: '#ea580c', border: '#c2410c', text: 'white' };
      case 'high': return { bg: '#f59e0b', border: '#d97706', text: 'white' };
      case 'medium': return { bg: '#eab308', border: '#ca8a04', text: 'black' };
      case 'low': return { bg: '#10b981', border: '#059669', text: 'white' };
      default: return { bg: '#3b82f6', border: '#2563eb', text: 'white' };
    }
  }, []);

  const getProgressByStatus = useCallback((status?: string) => {
    const statusMap: Record<string, number> = {
      open: 0,
      pending: 0,
      planning: 10,
      scheduled: 20,
      in_progress: 50,
      review: 80,
      done: 100,
      completed: 100,
      closed: 100,
      archived: 100,
    };
    return statusMap[status || ''] || 0;
  }, []);

  const navigatePrevious = useCallback(() => {
    const days = zoomLevel === 'day' ? 7 : zoomLevel === 'week' ? 30 : 90;
    setViewStart(addDays(viewStart, -days));
    setViewEnd(addDays(viewEnd, -days));
  }, [viewEnd, viewStart, zoomLevel]);

  const navigateNext = useCallback(() => {
    const days = zoomLevel === 'day' ? 7 : zoomLevel === 'week' ? 30 : 90;
    setViewStart(addDays(viewStart, days));
    setViewEnd(addDays(viewEnd, days));
  }, [viewEnd, viewStart, zoomLevel]);

  const goToToday = useCallback(() => {
    setViewStart(startOfMonth(new Date()));
    setViewEnd(endOfMonth(new Date()));
  }, []);

  const handleZoom = useCallback((level: 'day' | 'week' | 'month') => {
    setZoomLevel(level);
    const today = new Date();
    if (level === 'day') {
      setViewStart(addDays(today, -7));
      setViewEnd(addDays(today, 7));
    } else if (level === 'week') {
      setViewStart(addDays(today, -30));
      setViewEnd(addDays(today, 30));
    } else {
      setViewStart(addDays(today, -90));
      setViewEnd(addDays(today, 90));
    }
  }, []);

  const renderDayHeaders = useCallback(() => {
    const headers: React.ReactElement[] = [];
    const totalDays = timelineDays.length;
    const dayWidth = 100 / totalDays;

    if (zoomLevel === 'month') {
      let currentMonth = '';
      timelineDays.forEach((day, index) => {
        const monthName = format(day, 'MMM yyyy');
        if (monthName !== currentMonth) {
          currentMonth = monthName;
          const daysInMonth = timelineDays.filter((value) => format(value, 'MMM yyyy') === monthName).length;
          headers.push(
            <div
              key={`month-${index}`}
              style={{ width: `${daysInMonth * dayWidth}%` }}
              className="border-r border-gray-200 dark:border-gray-700 text-center font-medium text-gray-700 dark:text-gray-300 py-2"
            >
              {monthName}
            </div>
          );
        }
      });
      return headers;
    }

    timelineDays.forEach((day, index) => {
      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
      const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

      headers.push(
        <div
          key={`day-${index}`}
          style={{
            width: `${dayWidth}%`,
            ...(isToday ? { backgroundColor: activeTypeAccent.tintStrong, color: activeTypeAccent.color } : {}),
          }}
          className={`border-r border-gray-200 dark:border-gray-700 text-center text-xs py-2 ${
            isWeekend ? 'bg-gray-50 dark:bg-gray-800/50' : ''
          } ${isToday ? 'font-bold' : 'text-gray-600 dark:text-gray-400'}`}
        >
          <div>{format(day, 'EEE')}</div>
          <div className="font-medium">{format(day, 'd')}</div>
        </div>
      );
    });

    return headers;
  }, [activeTypeAccent.color, activeTypeAccent.tintStrong, timelineDays, zoomLevel]);

  const renderTodayMarker = useCallback(() => {
    const today = new Date();
    if (today < viewStart || today > viewEnd) return null;

    const totalDays = Math.max(1, differenceInDays(viewEnd, viewStart));
    const daysSinceStart = differenceInDays(today, viewStart);
    const leftPercent = (daysSinceStart / totalDays) * 100;

    return (
      <div
        className="absolute top-0 bottom-0 z-10 w-0.5 pointer-events-none"
        style={{ left: `${leftPercent}%`, backgroundColor: activeTypeAccent.color }}
      >
        <div className="absolute -top-2 -left-3 h-6 w-6 rounded-full border-2 border-white shadow-md" style={{ backgroundColor: activeTypeAccent.color }} />
      </div>
    );
  }, [activeTypeAccent.color, viewEnd, viewStart]);

  const getBarPosition = useCallback((item: TimelineItem) => {
    const totalDays = Math.max(1, differenceInDays(viewEnd, viewStart));
    const startOffset = Math.max(0, differenceInDays(item.startDate, viewStart));
    const duration = Math.max(1, differenceInDays(item.endDate, item.startDate) + 1);
    const leftPct = startOffset / totalDays;
    const widthPct = Math.min(duration / totalDays, 1 - leftPct);

    return {
      leftPx: leftPct * containerWidth,
      widthPx: widthPct * containerWidth,
    };
  }, [containerWidth, viewEnd, viewStart]);

  const renderDependencyArrows = useCallback(() => {
    if (!showDeps || dependencies.length === 0 || containerWidth === 0) return null;

    const itemIndexMap = new Map(displayItems.map((item, index) => [item.id, index]));
    const arrows: React.ReactNode[] = [];

    for (let index = 0; index < dependencies.length; index += 1) {
      const dependency = dependencies[index];
      const predecessorId = dependency.type === 'depends_on' ? dependency.targetId : dependency.sourceId;
      const successorId = dependency.type === 'depends_on' ? dependency.sourceId : dependency.targetId;

      const predecessorIndex = itemIndexMap.get(predecessorId);
      const successorIndex = itemIndexMap.get(successorId);
      if (predecessorIndex === undefined || successorIndex === undefined) continue;

      const predecessor = displayItems[predecessorIndex];
      const successor = displayItems[successorIndex];
      const predecessorPosition = getBarPosition(predecessor);
      const successorPosition = getBarPosition(successor);

      const x1 = predecessorPosition.leftPx + predecessorPosition.widthPx;
      const y1 = predecessorIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
      const x2 = successorPosition.leftPx;
      const y2 = successorIndex * ROW_HEIGHT + ROW_HEIGHT / 2;

      const isCritical = showCriticalPath && criticalPathIds.has(predecessorId) && criticalPathIds.has(successorId);
      const color = isCritical ? '#ef4444' : '#6366f1';
      const strokeWidth = isCritical ? 2.5 : 1.5;
      const dx = Math.abs(x2 - x1);
      const offset = Math.max(20, Math.min(dx * 0.4, 60));

      let path: string;
      if (x2 > x1 + 10) {
        path = `M ${x1} ${y1} C ${x1 + offset} ${y1}, ${x2 - offset} ${y2}, ${x2} ${y2}`;
      } else {
        const midY = (y1 + y2) / 2;
        const routeX = Math.max(x1, x2) + 30;
        path = `M ${x1} ${y1} L ${routeX} ${y1} Q ${routeX + 10} ${y1}, ${routeX + 10} ${midY} L ${routeX + 10} ${y2} Q ${routeX + 10} ${y2}, ${routeX} ${y2} L ${x2} ${y2}`;
      }

      arrows.push(
        <g key={`dep-${index}`}>
          <path d={path} fill="none" stroke="white" strokeWidth={strokeWidth + 2} opacity={0.3} />
          <path
            d={path}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={isCritical ? '' : '6 3'}
            markerEnd={isCritical ? 'url(#arrow-critical)' : 'url(#arrow-dep)'}
            opacity={0.85}
          />
        </g>
      );
    }

    const svgHeight = displayItems.length * ROW_HEIGHT;

    return (
      <svg
        className="absolute inset-0 pointer-events-none"
        style={{ width: containerWidth, height: svgHeight, zIndex: 25 }}
        viewBox={`0 0 ${containerWidth} ${svgHeight}`}
      >
        <defs>
          <marker id="arrow-dep" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill="#6366f1" />
          </marker>
          <marker id="arrow-critical" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#ef4444" />
          </marker>
        </defs>
        {arrows}
      </svg>
    );
  }, [containerWidth, criticalPathIds, dependencies, displayItems, getBarPosition, showCriticalPath, showDeps]);

  const handleDragStart = useCallback((event: React.MouseEvent, item: TimelineItem, type: DragState['type']) => {
    event.stopPropagation();
    event.preventDefault();
    setDragState({
      itemId: item.id,
      type,
      startX: event.clientX,
      originalStartDate: item.startDate,
      originalEndDate: item.endDate,
    });
  }, []);

  useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (event: MouseEvent) => {
      if (!timelineContainerRef.current) return;
      const totalDays = Math.max(1, differenceInDays(viewEnd, viewStart));
      const pxPerDay = containerWidth / totalDays;
      const deltaPx = event.clientX - dragState.startX;
      const deltaDays = Math.round(deltaPx / pxPerDay);

      let nextStart = dragState.originalStartDate;
      let nextEnd = dragState.originalEndDate;

      if (dragState.type === 'move') {
        nextStart = addDays(dragState.originalStartDate, deltaDays);
        nextEnd = addDays(dragState.originalEndDate, deltaDays);
      } else if (dragState.type === 'resize-start') {
        nextStart = addDays(dragState.originalStartDate, deltaDays);
        if (nextStart >= nextEnd) nextStart = addDays(nextEnd, -1);
      } else {
        nextEnd = addDays(dragState.originalEndDate, deltaDays);
        if (nextEnd <= nextStart) nextEnd = addDays(nextStart, 1);
      }

      setDragPreview({ itemId: dragState.itemId, startDate: nextStart, endDate: nextEnd });
      setHoveredItem(dragState.itemId);
    };

    const handleMouseUp = (event: MouseEvent) => {
      const totalDays = Math.max(1, differenceInDays(viewEnd, viewStart));
      const pxPerDay = containerWidth / totalDays;
      const deltaPx = event.clientX - dragState.startX;
      const deltaDays = Math.round(deltaPx / pxPerDay);

      if (deltaDays !== 0) {
        let nextStart = dragState.originalStartDate;
        let nextEnd = dragState.originalEndDate;

        if (dragState.type === 'move') {
          nextStart = addDays(dragState.originalStartDate, deltaDays);
          nextEnd = addDays(dragState.originalEndDate, deltaDays);
        } else if (dragState.type === 'resize-start') {
          nextStart = addDays(dragState.originalStartDate, deltaDays);
          if (nextStart >= nextEnd) nextStart = addDays(nextEnd, -1);
        } else {
          nextEnd = addDays(dragState.originalEndDate, deltaDays);
          if (nextEnd <= nextStart) nextEnd = addDays(nextStart, 1);
        }

        const startValue = format(nextStart, 'yyyy-MM-dd');
        const endValue = format(nextEnd, 'yyyy-MM-dd');
        onUpdateDates(dragState.itemId, startValue, endValue);
        onDateCascade?.(dragState.itemId, startValue, endValue);
      }

      setDragPreview(null);
      setDragState(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [containerWidth, dragState, onDateCascade, onUpdateDates, viewEnd, viewStart]);

  if (loading) {
    return (
      <div
        className={`flex flex-col overflow-hidden rounded-xl border ${presetViews.containerBg} ${presetViews.containerBorder}`}
        style={{ borderColor: activeTypeAccent.border, boxShadow: `0 12px 28px ${activeTypeAccent.shadow}` }}
      >
        <div
          className={`flex items-center justify-between gap-3 border-b px-4 py-3 ${presetViews.headerBg}`}
          style={{ borderColor: activeTypeAccent.border, backgroundImage: activeTypeAccent.surfaceGradient }}
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl" style={{ backgroundColor: activeTypeAccent.tintStrong }} />
            <div className="space-y-2">
              <div className="h-4 w-40 rounded-full bg-white/40 dark:bg-white/10" />
              <div className="h-3 w-24 rounded-full bg-white/25 dark:bg-white/5" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-8 w-16 rounded-full bg-white/30 dark:bg-white/10" />
            <div className="h-8 w-16 rounded-full bg-white/30 dark:bg-white/10" />
            <div className="h-8 w-16 rounded-full bg-white/30 dark:bg-white/10" />
          </div>
        </div>
        <div className="grid gap-4 p-4 lg:grid-cols-[280px_minmax(0,1fr)]">
          <div className="lg:col-span-2">
            <InlineLoadingPill label="Loading timeline..." primaryColor={activeTypeAccent.color} />
          </div>
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`timeline-loading-list-${index}`} className="h-[52px] animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800/60" />
            ))}
          </div>
          <div className="space-y-3">
            <div className="h-12 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800/60" />
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`timeline-loading-row-${index}`} className="relative h-[52px] overflow-hidden rounded-xl bg-gray-50 dark:bg-gray-900/40">
                <div
                  className="absolute inset-y-3 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700/70"
                  style={{ left: `${12 + index * 7}%`, width: `${28 + (index % 3) * 10}%` }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (timelineItems.length === 0) {
    return (
      <div
        className={`flex min-h-[26rem] items-center justify-center rounded-xl border ${presetViews.containerBg} ${presetViews.containerBorder}`}
        style={{ borderColor: activeTypeAccent.border, boxShadow: `0 12px 28px ${activeTypeAccent.shadow}` }}
      >
        <div className="max-w-sm px-6 text-center">
          <div
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border"
            style={{ borderColor: activeTypeAccent.border, backgroundColor: activeTypeAccent.tintStrong }}
          >
            <Calendar className="h-8 w-8" style={{ color: activeTypeAccent.color }} />
          </div>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">No dated work items to display</p>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Add start or due dates to work items to visualize sequencing, lead time, and scheduling overlap here.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col h-full ${presetViews.containerBg} rounded-lg border ${presetViews.containerBorder} ${dragState ? 'select-none' : ''}`}
      style={{ borderColor: activeTypeAccent.border, boxShadow: `0 12px 28px ${activeTypeAccent.shadow}` }}
    >
      <div
        className={`flex flex-col gap-2 border-b p-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-4 sm:py-3 ${presetViews.dividerBorder} ${presetViews.headerBg}`}
        style={getWorkItemsAccentPanelStyle(activeTypeAccent, 'strong')}
      >
        <div className="flex items-center justify-between sm:justify-start gap-1 sm:gap-2">
          <div className="flex items-center gap-1">
            <button onClick={navigatePrevious} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors touch-manipulation" title="Previous">
              <ChevronLeft className={`w-5 h-5 ${presetViews.bodyText}`} />
            </button>
            <button
              onClick={goToToday}
              className="rounded px-3 py-2 text-sm font-medium text-white transition-colors hover:opacity-90 touch-manipulation"
              style={{ backgroundColor: activeTypeAccent.color, boxShadow: `0 10px 20px ${activeTypeAccent.shadow}` }}
            >
              Today
            </button>
            <button onClick={navigateNext} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors touch-manipulation" title="Next">
              <ChevronRight className={`w-5 h-5 ${presetViews.bodyText}`} />
            </button>
          </div>
          <span className={`text-xs sm:text-sm font-medium ${presetViews.bodyText} ${isMobile ? '' : 'ml-4'}`}>{rangeLabel}</span>
          {activeItemType && !isMobile && (
            <WorkItemsTypeAccentBadge label={resolvedActiveTypeLabel} accent={activeTypeAccent} className="ml-3" />
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
          {dependencies.length > 0 && !isMobile && (
            <>
              <button
                onClick={() => setShowDeps(!showDeps)}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs transition-colors ${
                  showDeps ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300' : `${presetViews.buttonBg} ${presetViews.buttonText}`
                }`}
                title="Toggle dependency arrows"
              >
                <GitBranch className="w-3.5 h-3.5" />
                Deps
              </button>
              {showCriticalPath && (
                <button className="flex items-center gap-1.5 px-2 py-1.5 rounded text-xs bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300" title="Critical path highlighted">
                  <Route className="w-3.5 h-3.5" />
                  Critical
                </button>
              )}
              <div className="h-4 w-px bg-gray-300 dark:bg-gray-600" />
            </>
          )}

          {!isMobile && <span className={`text-sm ${presetViews.mutedText} mr-1`}>Zoom:</span>}
          {(['day', 'week', 'month'] as const).map((level) => (
            <button
              key={level}
              onClick={() => handleZoom(level)}
              className={`px-2 sm:px-3 py-1.5 rounded text-xs sm:text-sm transition-colors touch-manipulation flex-shrink-0 ${
                zoomLevel === level ? 'text-white' : `${presetViews.buttonBg} ${presetViews.buttonText}`
              }`}
              style={zoomLevel === level ? { backgroundColor: activeTypeAccent.color, boxShadow: `0 10px 20px ${activeTypeAccent.shadow}` } : undefined}
            >
              {isMobile ? level[0].toUpperCase() : `${level.charAt(0).toUpperCase()}${level.slice(1)}`}
            </button>
          ))}
        </div>
      </div>

      <div className={`flex flex-wrap items-center gap-2 border-b px-3 py-2 sm:px-4 ${presetViews.rowBg}`} style={getWorkItemsAccentPanelStyle(activeTypeAccent, 'soft')}>
        {activeItemType && !hasDesktopToolbarActiveType && (
          <WorkItemsTypeAccentBadge label={resolvedActiveTypeLabel} accent={activeTypeAccent} />
        )}
        <span className={summaryBadgeClassName} style={{ borderColor: activeTypeAccent.border }}>
          <span>{displayItems.length}</span>
          <span className="opacity-70">scheduled</span>
        </span>
        {isMobile && (
          <span className={summaryBadgeClassName} style={{ borderColor: activeTypeAccent.border }}>
            <span>{rangeLabel}</span>
          </span>
        )}
        {visibleDependencyCount > 0 && (
          <span className={summaryBadgeClassName} style={{ borderColor: activeTypeAccent.border }}>
            <GitBranch className="h-3.5 w-3.5" />
            <span>{visibleDependencyCount} dependencies</span>
          </span>
        )}
        {showCriticalPath && criticalPathIds.size > 0 && (
          <span className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-[11px] font-semibold text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300">
            <Route className="h-3.5 w-3.5" />
            <span>{criticalPathIds.size} critical</span>
          </span>
        )}
        {!isMobile && (
          <div className={`ml-auto inline-flex items-center gap-2 text-[11px] ${presetViews.mutedText}`}>
            <Info className="h-3.5 w-3.5" />
            <span>Drag bars to move dates; resize handles appear on hover.</span>
          </div>
        )}
      </div>

      {isMobile ? (
        <div className="flex-1 overflow-auto">
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {displayItems.map((item) => {
              const progress = item.progress_percentage || getProgressByStatus(item.status);
              const isCritical = criticalPathIds.has(item.id);
              const typeAccent = getWorkItemTypeAccent(item.item_type, primaryColor);
              const typeIdentity = getWorkItemTypeIdentity(item.item_type, primaryColor);
              const { label, durationLabel } = formatItemRange(item);

              return (
                <WorkItemHoverCard
                  key={item.id}
                  as="button"
                  workItem={item}
                  className="flex w-full flex-col gap-2 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  onClick={() => onViewItem(item)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium"
                          style={{ borderColor: typeAccent.border, backgroundColor: typeAccent.tintStrong, color: typeIdentity.color }}
                        >
                          {item.item_type}
                        </span>
                        <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400">#{item.item_number}</span>
                        {isCritical && (
                          <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-600 dark:bg-red-900/30 dark:text-red-300">Critical</span>
                        )}
                      </div>
                      <div className="mt-1 truncate text-sm font-semibold text-gray-900 dark:text-white">{item.title}</div>
                    </div>
                    <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">{progress}%</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 dark:bg-gray-800">
                      <Calendar className="h-3 w-3" />
                      {label}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 dark:bg-gray-800">{durationLabel}</span>
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 capitalize dark:bg-gray-800">{item.status || 'open'}</span>
                  </div>

                  <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                    <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: isCritical ? '#ef4444' : typeIdentity.color }} />
                  </div>
                </WorkItemHoverCard>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="flex">
            <div className={`w-80 flex-shrink-0 border-r ${presetViews.containerBorder} ${presetViews.headerBg}`}>
              <div className={`sticky top-0 ${presetViews.rowBg} border-b ${presetViews.containerBorder} px-3 py-2.5 ${presetViews.bodyText} z-10 flex items-center justify-between`}>
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">Work items</span>
                {visibleDependencyCount > 0 && (
                  <span className="text-[11px] font-medium text-indigo-500 dark:text-indigo-400">
                    {visibleDependencyCount} dep{visibleDependencyCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {displayItems.map((item, index) => {
                const isGroupStart = groupByType && (index === 0 || displayItems[index - 1].item_type !== item.item_type);
                const isCritical = criticalPathIds.has(item.id);
                const isHighlighted = highlightedItemId === item.id;
                const { label, durationLabel } = formatItemRange(item);

                return (
                  <React.Fragment key={item.id}>
                    {isGroupStart && (
                      <div
                        className={`flex items-center justify-between px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${presetViews.headerBg} border-b ${presetViews.containerBorder} cursor-pointer`}
                        onClick={() => {
                          setCollapsedGroups((previous) => {
                            const next = new Set(previous);
                            if (next.has(item.item_type)) {
                              next.delete(item.item_type);
                            } else {
                              next.add(item.item_type);
                            }
                            return next;
                          });
                        }}
                      >
                        <span style={{ color: getWorkItemTypeIdentity(item.item_type, primaryColor).color }}>{item.item_type}s</span>
                        <ChevronRight className={`w-3 h-3 transition-transform ${!collapsedGroups.has(item.item_type) ? 'rotate-90' : ''}`} />
                      </div>
                    )}

                    <WorkItemHoverCard
                      as="div"
                      workItem={item}
                      className={`border-b px-3 py-2 ${presetViews.containerBorder} cursor-pointer ${presetViews.rowHoverBg} transition-colors touch-manipulation ${isCritical ? 'border-l-2 border-l-red-500' : ''}`}
                      style={{
                        height: `${ROW_HEIGHT}px`,
                        ...(hoveredItem === item.id || isHighlighted ? { backgroundColor: activeTypeAccent.tint } : {}),
                      }}
                      onMouseEnter={() => setHoveredItem(item.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                      onClick={() => onViewItem(item)}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        {item.isMilestone ? (
                          <span className="px-1.5 py-0.5 text-xs rounded bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 flex items-center gap-1">
                            <Diamond className="w-3 h-3" /> milestone
                          </span>
                        ) : (
                          <span
                            className="px-1.5 py-0.5 text-[11px] rounded"
                            style={{
                              backgroundColor: getWorkItemTypeAccent(item.item_type, primaryColor).tintStrong,
                              color: getWorkItemTypeIdentity(item.item_type, primaryColor).color,
                            }}
                          >
                            {item.item_type}
                          </span>
                        )}
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">#{item.item_number}</span>
                        {isCritical && <span className="text-[9px] font-bold text-red-500 dark:text-red-400">CRITICAL</span>}
                      </div>
                      <div className="mt-0.5 line-clamp-1 text-[13px] font-semibold text-gray-900 dark:text-white">{item.title}</div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                        <span>{label}</span>
                        <span>•</span>
                        <span>{durationLabel}</span>
                      </div>
                    </WorkItemHoverCard>
                  </React.Fragment>
                );
              })}
            </div>

            <div className="flex-1 relative" ref={timelineContainerRef}>
              <div className={`sticky top-0 ${presetViews.containerBg} border-b ${presetViews.containerBorder} flex z-30`}>
                {renderDayHeaders()}
              </div>

              <div className="relative">
                {renderDependencyArrows()}

                {displayItems.map((item) => {
                  const template = getTemplate(item);
                  const colors = getColorByPriority(item.priority, template);
                  const barStyle = getBarStyle(item);
                  const progress = item.progress_percentage || getProgressByStatus(item.status);
                  const isCritical = criticalPathIds.has(item.id);
                  const isDragging = dragState?.itemId === item.id;

                  if (item.isMilestone) {
                    return (
                      <div
                        key={item.id}
                        className="relative border-b border-gray-200 dark:border-gray-700"
                        style={{ height: `${ROW_HEIGHT}px` }}
                        onMouseEnter={() => setHoveredItem(item.id)}
                        onMouseLeave={() => setHoveredItem(null)}
                      >
                        <div className="absolute inset-0 flex">
                          {timelineDays.map((day, index) => {
                            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                            const dayWidth = 100 / timelineDays.length;
                            return (
                              <div
                                key={index}
                                style={{ width: `${dayWidth}%` }}
                                className={`border-r border-gray-100 dark:border-gray-700/50 ${isWeekend ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''}`}
                              />
                            );
                          })}
                        </div>

                        <div className="absolute top-1/2 -translate-y-1/2 z-20 cursor-pointer" style={{ left: barStyle.left, marginLeft: '-10px' }} onClick={() => onViewItem(item)}>
                          <div className={`w-5 h-5 rotate-45 border-2 ${isCritical ? 'bg-red-500 border-red-700' : 'bg-amber-400 border-amber-600'} shadow-md`} />
                          <div className={`absolute top-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium ${isCritical ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-300'}`}>
                            {item.title}
                          </div>
                        </div>

                        <div className="absolute top-1/2 -translate-y-1/2 z-20" style={{ left: barStyle.left, marginLeft: '16px' }}>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">{format(item.endDate, 'MMM d')}</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={item.id}
                      className="relative border-b border-gray-200 dark:border-gray-700"
                      style={{ height: `${ROW_HEIGHT}px` }}
                      onMouseEnter={() => setHoveredItem(item.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      <div className="absolute inset-0 flex">
                        {timelineDays.map((day, index) => {
                          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                          const dayWidth = 100 / timelineDays.length;
                          return (
                            <div
                              key={index}
                              style={{ width: `${dayWidth}%` }}
                              className={`border-r border-gray-100 dark:border-gray-700/50 ${isWeekend ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''}`}
                            />
                          );
                        })}
                      </div>

                      <WorkItemHoverCard
                        as="div"
                        workItem={item}
                        className={`absolute top-1/2 -translate-y-1/2 h-7 rounded-md shadow-sm transition-all z-20 group ${
                          isDragging ? 'shadow-lg ring-2 ring-blue-400 ring-offset-1' : 'hover:shadow-md'
                        } ${isCritical ? 'ring-1 ring-red-400/50' : ''}`}
                        style={{
                          ...barStyle,
                          backgroundColor: isCritical ? '#fee2e2' : colors.bg,
                          borderLeft: `3px solid ${isCritical ? '#ef4444' : colors.border}`,
                          opacity: hoveredItem === item.id || isDragging ? 1 : 0.9,
                          cursor: isDragging ? 'grabbing' : 'grab',
                        }}
                        onMouseDown={(event) => handleDragStart(event, item, 'move')}
                        onClick={() => { if (!dragState) onViewItem(item); }}
                      >
                        <div className="absolute inset-0 rounded-md overflow-hidden">
                          <div className="h-full bg-white/20 transition-all" style={{ width: `${progress}%` }} />
                        </div>

                        <div className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize opacity-0 group-hover:opacity-100 hover:bg-black/10 rounded-l-md transition-opacity z-30" onMouseDown={(event) => handleDragStart(event, item, 'resize-start')} />
                        <div className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize opacity-0 group-hover:opacity-100 hover:bg-black/10 rounded-r-md transition-opacity z-30" onMouseDown={(event) => handleDragStart(event, item, 'resize-end')} />

                        <div className="absolute inset-0 flex items-center px-3 text-xs font-medium overflow-hidden pointer-events-none" style={{ color: isCritical ? '#991b1b' : colors.text }}>
                          <span className="truncate">
                            {template?.icon && <span className="mr-1">{template.icon}</span>}
                            {item.item_number}: {item.title}
                          </span>
                          {progress > 0 && progress < 100 && (
                            <span className="ml-auto pl-2 text-[10px] opacity-70 flex-shrink-0">{progress}%</span>
                          )}
                        </div>
                      </WorkItemHoverCard>
                    </div>
                  );
                })}

                {renderTodayMarker()}
              </div>
            </div>
          </div>
        </div>
      )}

      {!isMobile && (
        <div className={`flex flex-wrap items-center gap-2 sm:gap-4 p-2 sm:p-3 ${presetViews.headerBg} border-t ${presetViews.containerBorder}`} style={{ borderColor: activeTypeAccent.border }}>
          {activeItemType && (
            <div className="flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: activeTypeAccent.border, backgroundColor: activeTypeAccent.tint }}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: activeTypeAccent.color }} />
              <span className={`text-[10px] font-semibold ${presetViews.bodyText}`}>{resolvedActiveTypeLabel}</span>
            </div>
          )}
          <span className={`text-xs font-medium ${presetViews.bodyText}`}>Priority:</span>
          {['low', 'medium', 'high', 'urgent', 'critical'].map((priority) => {
            const colors = getColorByPriority(priority);
            return (
              <div key={priority} className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.bg }} />
                <span className={`text-[10px] ${presetViews.mutedText} capitalize`}>{priority}</span>
              </div>
            );
          })}
          <div className="h-3 w-px bg-gray-300 dark:bg-gray-600" />
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded-full" style={{ backgroundColor: activeTypeAccent.color }} />
            <span className={`text-[10px] ${presetViews.mutedText}`}>Today</span>
          </div>
          {dependencies.length > 0 && (
            <>
              <div className="h-3 w-px bg-gray-300 dark:bg-gray-600" />
              <div className="flex items-center gap-1">
                <div className="w-4 h-0 border-t-2 border-dashed border-indigo-500" />
                <span className={`text-[10px] ${presetViews.mutedText}`}>Dependency</span>
              </div>
              <div className="flex items-center gap-1">
                <Diamond className="w-3 h-3 text-amber-500" />
                <span className={`text-[10px] ${presetViews.mutedText}`}>Milestone</span>
              </div>
              {showCriticalPath && criticalPathIds.size > 0 && (
                <div className="flex items-center gap-1">
                  <div className="w-4 h-0 border-t-2 border-red-500" />
                  <span className="text-[10px] text-red-500 font-medium">Critical Path ({criticalPathIds.size})</span>
                </div>
              )}
            </>
          )}
          <div className="ml-auto flex items-center gap-1">
            <span className={`text-[10px] ${presetViews.mutedText}`}>Resize handles appear on hover</span>
          </div>
        </div>
      )}
    </div>
  );
}
